/* eslint-disable react-hooks/exhaustive-deps */
import {FC, createContext, useContext, useState, useEffect, useMemo, ReactNode} from 'react'
import {useQuery} from 'react-query'
import {
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  QueryResponseContextProps,
  stringifyRequestQuery,
} from '../../../../../../_metronic/helpers'
import {getCustomers} from './_requests'
import {Customer} from './_models'
import {useQueryRequest} from './QueryRequestProvider'
import {useAuth} from '../../../../auth'
import {isAdminWithoutSelectedCompany} from '../../../shared/admin-company-selector/AdminCompanySelector'

// isError: the list could not be read; it used to look like «خریدار یافت نشد».
type CustomersResponseContextProps = QueryResponseContextProps<Customer> & {isError: boolean}
const QueryResponseContext = createContext<CustomersResponseContextProps>({
  ...initialQueryResponse,
  isError: false,
})
type WithChildren = {
  children?: ReactNode
}
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state, updateState} = useQueryRequest()
  const {currentUser} = useAuth()
  // An admin's buyers list waits for a business: without one the server has no list to read.
  const waitsForBusiness = isAdminWithoutSelectedCompany(currentUser)
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  useEffect(() => {
    updateState({
      ...state,
    })
  }, [])

  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.CUSTOMERS_LIST}-${query}`,
    () => {
      return getCustomers(query)
    },
    {
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !waitsForBusiness,
      retry: 1,
    }
  )

  return (
    <QueryResponseContext.Provider
      value={{isLoading: isFetching, isError: isError && !isFetching, refetch, response, query}}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  if (!response) {
    return []
  }

  return response?.data || []
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  return response.payload.pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
